/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link as ReactRouterLink } from 'react-router';

import { Button, Typography, Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import ProductEnrolledCard from 'bundles/course-cards/components/course-card/enterprise/ProductEnrolledCard';
import { Heading, Section } from 'bundles/enterprise-legacy-learner-home/components/AutoHeading';
import SingleProgramBrowseCTASvg from 'bundles/enterprise-legacy-learner-home/svg/SingleProgramBrowseCTASvg';
import type { AddedProps as PropsFromNaptimeContainer } from 'bundles/naptimejs';
import { useNaptime } from 'bundles/naptimejs';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import ProgramCurriculumProductsV1 from 'bundles/naptimejs/resources/programCurriculumProducts.v1';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<ReactRouterLink>>(
  // TODO(ppaskaris): Ask how to type this.
  // @ts-expect-error
  Button
);

type PropsFromCaller = {
  programId: string | undefined;
  thirdPartyOrganizationId: string;
  viewMyCoursesUrl: string;
  userId: number;
};

type PropsFromNaptime = PropsFromNaptimeContainer & {
  enrolledCourses?: ProgramCurriculumProductsV1[];
};

type PropsFromWithProps = {
  loading: boolean;
  inProgressCourse: ProgramCurriculumProductsV1 | undefined;
};

export type Props = PropsFromCaller & PropsFromWithProps;

export const MyRecentActivityBaseComponent = ({
  programId,
  thirdPartyOrganizationId,
  viewMyCoursesUrl,
  userId,
  inProgressCourse,
  loading,
}: Props) => {
  const styles = {
    headingContainer: css`
      display: flex;
      flex-flow: row nowrap;
      align-items: flex-end;
      margin-bottom: var(--cds-spacing-200);
    `,
    heading: css`
      flex: 1 0 auto;
    `,
    viewMyCoursesButton: css`
      flex: 0 0 auto;
      margin-left: -4px 0 -4px var(--cds-spacing-200);
      padding: var(--cds-spacing-100);
    `,
    heightContainer: css`
      min-height: 158px;
    `,
    emptyContainer: css`
      min-height: 158px;
      box-shadow:
        0 1px 6px var(--cds-color-grey-50),
        0 1px 4px var(--cds-color-grey-50);
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: center;
      padding: var(--cds-spacing-300);
    `,
    emptyIcon: css`
      flex: 0 0 auto;
      margin-right: var(--cds-spacing-300);
    `,
    emptyBody: css`
      flex: 1 1 auto;
    `,
    emptyBodyHeading: css`
      margin-bottom: var(--cds-spacing-150);
    `,
    emptyBodyDescription: css`
      max-width: 712px;
    `,
  };

  const isCourseCompleted = inProgressCourse?.isCompleted;
  return (
    <>
      <div css={styles.headingContainer} data-testid="my-recent-activity">
        <Heading defaultLevel={2} css={styles.heading}>
          {_t('My Recent Activity')}
        </Heading>
        <TrackedButton
          {...Button.defaultProps}
          variant="ghost"
          size="medium"
          icon={<ArrowNextIcon size="medium" />}
          iconPosition="after"
          component={ReactRouterLink}
          to={viewMyCoursesUrl}
          trackingName="my_recent_activity_in_progress_course_view_my_courses_button"
          css={styles.viewMyCoursesButton}
          disabled={loading}
        >
          {_t('View my courses')}
        </TrackedButton>
      </div>
      <Section initialLevel={3}>
        <div css={styles.heightContainer}>
          {!loading && (!inProgressCourse || isCourseCompleted) && (
            <div css={styles.emptyContainer}>
              <SingleProgramBrowseCTASvg height={88} css={styles.emptyIcon} />
              <div css={styles.emptyBody}>
                {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
                <Typography component="div" variant="h2semibold" css={styles.emptyBodyHeading}>
                  {_t("Let's begin learning!")}
                </Typography>
                <Typography2 component="p" css={styles.emptyBodyDescription}>
                  {_t(
                    'Your in progress course will be displayed here to make it easy to pick up where you left off. Get started by browsing the curated collections, searching the entire catalog, or choosing a category to find a course.'
                  )}
                </Typography2>
              </div>
            </div>
          )}
          {inProgressCourse && programId != null && !isCourseCompleted && (
            <ProductEnrolledCard
              userId={userId}
              programId={programId}
              product={inProgressCourse}
              thirdPartyOrganizationId={thirdPartyOrganizationId}
            />
          )}
        </div>
      </Section>
    </>
  );
};

const MyRecentActivity = ({ programId, thirdPartyOrganizationId, viewMyCoursesUrl, userId }: PropsFromCaller) => {
  const naptimeResponse = useNaptime<PropsFromNaptime>(() => ({
    enrolledCourses: ProgramCurriculumProductsV1.finder('latestEnrolledCourseActivities', {
      params: { programId, limit: 1 },
      required: false,
    }),
  }));

  const {
    data: { enrolledCourses },
  } = naptimeResponse;
  const inProgressCourse = enrolledCourses?.[0];
  const loading = !enrolledCourses;

  return (
    <MyRecentActivityBaseComponent
      programId={programId}
      thirdPartyOrganizationId={thirdPartyOrganizationId}
      viewMyCoursesUrl={viewMyCoursesUrl}
      userId={userId}
      inProgressCourse={inProgressCourse}
      loading={loading}
    />
  );
};

export default MyRecentActivity;
